<template>
  <v-container fluid class="pa-4">
    <v-card v-if="overdueAgreements.length > 0" class="elevation-2 mb-4 od-card">
      <v-toolbar color="red darken-2" dark dense>
        <v-icon left>warning</v-icon>
        <v-toolbar-title>Vadesi Geçmiş Anlaşmalar</v-toolbar-title>
        <span class="od-count">{{ overdueAgreements.length }}</span>
        <v-spacer></v-spacer>
        <span class="od-sum">
          <span class="od-sum-k">Toplam</span>
          <span class="od-sum-v">{{ formatCurrency(overdueTotal) }}</span>
        </span>
      </v-toolbar>

      <div class="od-list">
        <div v-for="ag in overdueAgreements" :key="'od-' + ag.id" class="od-row">
          <div class="od-days">
            <div class="od-days-n">{{ ag.daysOverdue }}</div>
            <div class="od-days-l">gün</div>
          </div>
          <div class="od-main">
            <div class="od-firm">
              {{ ag.companyName }}
              <span v-if="ag.contactPerson" class="od-person">{{ ag.contactPerson }}</span>
            </div>
            <div class="od-meta">
              Vade {{ formatDate(ag.paymentDate) }} · {{ ag.orderCount }} sipariş
            </div>
          </div>
          <div class="od-cash">
            <div class="od-cash-v">{{ formatCurrency(ag.totalNetCash) }}</div>
            <div class="od-cash-l">net cash</div>
          </div>
          <div class="od-actions">
            <v-btn small color="success" depressed class="od-btn ma-0 mr-2" @click="openMarkPaidDialog(ag)">
              <v-icon left small>check_circle</v-icon>
              Ödendi
            </v-btn>
            <v-btn small color="deep-purple" dark depressed class="od-btn ma-0" @click="openPartialPayDialog(ag)">
              <v-icon left small>call_split</v-icon>
              Parçalı
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <v-card class="elevation-2">
      <v-toolbar color="amber darken-3" dark dense>
        <v-icon left>handshake</v-icon>
        <v-toolbar-title>Darphane Anlaşmaları</v-toolbar-title>
        <v-chip
          v-if="computeByCompany"
          small
          color="indigo"
          text-color="white"
          class="ml-3"
        >
          🏢 Şirket bazlı hesaplanıyor
        </v-chip>
      </v-toolbar>

      <v-tabs v-model="activeTab" color="amber lighten-4" slider-color="amber darken-3">
        <v-tab>📅 Para Alış Takvimi</v-tab>
        <v-tab>⏳ Bekleyenler</v-tab>
        <v-tab>📋 Geçmiş</v-tab>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm4 class="px-2">
                  <v-menu
                    v-model="planFromMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="planFrom"
                        label="Başlangıç Tarihi"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                      />
                    </template>
                    <v-date-picker
                      v-model="planFrom"
                      locale="tr-TR"
                      @input="planFromMenu = false; loadPlan()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm4 class="px-2">
                  <v-menu
                    v-model="planToMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="planTo"
                        label="Bitiş Tarihi"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                      />
                    </template>
                    <v-date-picker
                      v-model="planTo"
                      locale="tr-TR"
                      @input="planToMenu = false; loadPlan()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm4 class="px-2 pt-3">
                  <v-btn small color="amber darken-3" class="white--text" @click="loadPlan">
                    <v-icon left small>refresh</v-icon>
                    Yenile
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-layout v-if="plan" row wrap class="mb-3">
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Toplam Beklenen Net Nakit</div>
                    <div class="title font-weight-bold blue--text">
                      {{ formatCurrency(plan.grandTotalNetCash) }}
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Anlaşılmamış Sipariş</div>
                    <div class="title font-weight-bold orange--text">
                      {{ plan.unallocatedOrderCount }} ad.
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Devreden Sipariş</div>
                    <div class="title font-weight-bold red--text">
                      🔄 {{ plan.totalCarriedOverOrderCount }} ad.
                    </div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 class="px-2 mb-2">
                  <v-card outlined class="pa-3">
                    <div class="caption grey--text text--darken-1">Toplam Has Gram</div>
                    <div class="title font-weight-bold">
                      {{ formatGram(plan.grandTotalPureGram) }} gr
                    </div>
                  </v-card>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="planHeaders"
                :items="plan ? plan.days : []"
                :loading="loading"
                hide-actions
                class="elevation-1"
                expand
              >
                <template v-slot:items="props">
                  <tr :class="planRowClass(props.item)">
                    <td class="text-no-wrap">
                      <strong>{{ formatDateWithDay(props.item.paymentDate) }}</strong>
                      <span v-if="isToday(props.item.paymentDate)" class="ml-1 caption red--text">(Bugün)</span>
                    </td>
                    <td class="plan-mp-cell">
                      <div
                        v-for="mp in (props.item.marketplaces || [])"
                        :key="'plan-mp-' + props.item.paymentDate + '-' + mp.mpId"
                        class="plan-mp-row"
                      >
                        <span class="inv-chip" :class="marketplaceChipClass(mp.mpId)">{{ mp.mpName }} ×{{ mp.orderCount }}</span>
                        <span class="plan-mp-cash">{{ formatCurrency(mp.totalNetCash) }}</span>
                      </div>
                      <div v-if="planCompanyLabel(props.item)" class="inv-meta">{{ planCompanyLabel(props.item) }}</div>
                    </td>
                    <td class="text-xs-right">
                      {{ props.item.orderCount }}
                      <small class="grey--text">
                        ({{ props.item.allocatedOrderCount }}A / {{ props.item.unallocatedOrderCount }}B)
                      </small>
                      <v-chip
                        v-if="props.item.carriedOverOrderCount > 0"
                        small
                        color="orange"
                        text-color="white"
                        class="ml-1"
                      >
                        🔄 {{ props.item.carriedOverOrderCount }} devreden
                      </v-chip>
                    </td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalInvoiceAmount) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.totalNetCash) }}
                    </td>
                    <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }} gr</td>
                    <td class="plan-status-cell">
                      <template v-if="props.item.existingAgreements && props.item.existingAgreements.length > 0">
                        <div v-for="ag in props.item.existingAgreements" :key="ag.id">
                          <v-chip
                            small
                            :color="agreementChipColor(ag.status)"
                            text-color="white"
                            style="cursor: pointer"
                            @click="openDetailDialog(ag)"
                          >
                            {{ ag.statusName }} · {{ formatGramPrice(ag.goldGramPrice) }} ₺/gr
                          </v-chip>
                        </div>
                        <div v-if="props.item.unallocatedOrderCount > 0">
                          <v-chip small color="orange darken-2" text-color="white" class="font-weight-bold">
                            <v-icon left small>warning</v-icon>
                            {{ props.item.unallocatedOrderCount }} sipariş bekliyor
                          </v-chip>
                        </div>
                      </template>
                      <span v-else-if="props.item.unallocatedOrderCount > 0">
                        <v-chip small color="orange darken-2" text-color="white" class="font-weight-bold">
                          <v-icon left small>warning</v-icon>
                          Anlaşma yok · {{ props.item.unallocatedOrderCount }} sipariş
                        </v-chip>
                      </span>
                      <span v-else class="grey--text caption">—</span>
                    </td>
                    <td class="plan-action-cell">
                      <v-btn small color="primary" outline class="plan-action-btn" @click="openDayOrdersDialog(props.item)">
                        <v-icon left small>visibility</v-icon>
                        Detay
                      </v-btn>
                      <v-btn
                        small
                        color="success"
                        class="plan-action-btn"
                        :disabled="props.item.unallocatedOrderCount === 0"
                        @click="openCreateDialog(props.item)"
                      >
                        <v-icon left small>add_circle</v-icon>
                        Anlaşma Yap
                      </v-btn>
                    </td>
                  </tr>
                  <tr
                    v-if="props.item.unallocatedHasUnmatchedCity"
                    class="orange lighten-5"
                  >
                    <td colspan="8" class="text-no-wrap">
                      <v-icon small color="orange darken-2">warning</v-icon>
                      <span class="orange--text text--darken-3 caption ml-1">
                        Bu güne dahil bazı siparişlerin şehri tanımsız (duration=0 varsayıldı). DeliveryCities tablosunu güncelleyin.
                      </span>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Seçilen tarih aralığında plan bulunmamaktadır.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm4 class="px-2">
                  <v-select
                    v-model="marketplaceFilter"
                    :items="marketplaceOptions"
                    item-text="text"
                    item-value="value"
                    label="Pazaryeri"
                    prepend-icon="store"
                    dense
                    clearable
                  />
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="agreementHeaders"
                :items="filteredPendingAgreements"
                :loading="loading"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-no-wrap">{{ formatDate(props.item.agreementDate) }}</td>
                    <td>
                      {{ props.item.companyName }}
                      <v-chip v-if="props.item.isPartial" x-small color="deep-purple lighten-1" text-color="white" class="ml-1">Parçalı</v-chip>
                      <div v-if="props.item.ownerCompanyName" class="inv-meta">
                        🏢 {{ props.item.ownerCompanyName }}
                      </div>
                      <div v-if="agreementMarketplaces(props.item).length > 0" class="inv-meta">
                        <span
                          v-for="mp in agreementMarketplaces(props.item)"
                          :key="mp.mpId"
                          class="mr-1"
                          :class="marketplaceChipClass(mp.mpId)"
                        >{{ marketplaceLabel(mp) }} ×{{ mp.orderCount }}</span>
                      </div>
                    </td>
                    <td>
                      {{ props.item.contactPerson }}
                      <div v-if="props.item.createdByName" class="inv-meta">{{ props.item.createdByName }}</div>
                    </td>
                    <td class="text-no-wrap">{{ formatDate(props.item.paymentDate) }}</td>
                    <td class="text-xs-right">{{ props.item.orderCount }}</td>
                    <td class="text-xs-right">
                      {{ formatCurrency(props.item.totalInvoiceAmount) }}
                      <div class="inv-meta">{{ formatCurrency(props.item.totalNetCash) }}</div>
                    </td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.agreedAmount) }}
                      <span v-if="agreementExtra(props.item) > 0" class="caption green--text d-block">
                        +{{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                      <span v-else-if="agreementExtra(props.item) < 0" class="caption red--text d-block">
                        {{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                    </td>
                    <td class="text-xs-right">{{ formatGramPrice(props.item.goldGramPrice) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      <div class="inv-meta">{{ formatGram(props.item.totalPureGram) }}</div>
                      {{ formatGram(agreementAcquiredGram(props.item)) }}
                      <span v-if="agreementAcquiredDelta(props.item) > 0" class="caption green--text d-block">
                        +{{ formatGram(agreementAcquiredDelta(props.item)) }}
                      </span>
                      <span v-else-if="agreementAcquiredDelta(props.item) < 0" class="caption red--text d-block">
                        {{ formatGram(agreementAcquiredDelta(props.item)) }}
                      </span>
                    </td>
                    <td class="agr-action-cell">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="primary" v-on="on" @click="openDetailDialog(props.item)">
                            <v-icon small>visibility</v-icon>
                          </v-btn>
                        </template>
                        <span>Detay</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="amber darken-3" v-on="on" @click="openEditDialog(props.item)">
                            <v-icon small>edit</v-icon>
                          </v-btn>
                        </template>
                        <span>Düzenle</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="success" v-on="on" @click="openMarkPaidDialog(props.item)">
                            <v-icon small>check_circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Ödendi</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="deep-purple" v-on="on" @click="openPartialPayDialog(props.item)">
                            <v-icon small>call_split</v-icon>
                          </v-btn>
                        </template>
                        <span>Parçalı Öde</span>
                      </v-tooltip>
                      <v-tooltip bottom v-if="props.item.isPartial">
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="indigo" v-on="on" @click="openChainDialog(props.item)">
                            <v-icon small>account_tree</v-icon>
                          </v-btn>
                        </template>
                        <span>Parçalı Zinciri Gör</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="error" v-on="on" @click="openCancelDialog(props.item)">
                            <v-icon small>cancel</v-icon>
                          </v-btn>
                        </template>
                        <span>İptal</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Bekleyen anlaşma yok.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm3 class="px-2">
                  <v-menu
                    v-model="historyFromMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="historyFrom"
                        label="Başlangıç"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                        clearable
                        @click:clear="historyFrom = null; loadHistory()"
                      />
                    </template>
                    <v-date-picker
                      v-model="historyFrom"
                      locale="tr-TR"
                      @input="historyFromMenu = false; loadHistory()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-menu
                    v-model="historyToMenu"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="historyTo"
                        label="Bitiş"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        dense
                        clearable
                        @click:clear="historyTo = null; loadHistory()"
                      />
                    </template>
                    <v-date-picker
                      v-model="historyTo"
                      locale="tr-TR"
                      @input="historyToMenu = false; loadHistory()"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-select
                    v-model="historyStatus"
                    :items="historyStatusOptions"
                    label="Durum"
                    dense
                    clearable
                    @change="loadHistory"
                  />
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-select
                    v-model="historyRefineryId"
                    :items="historyRefineryOptions"
                    item-text="text"
                    item-value="value"
                    label="Rafineri"
                    prepend-icon="factory"
                    dense
                  />
                </v-flex>
                <v-flex xs12 sm3 class="px-2">
                  <v-select
                    v-model="marketplaceFilter"
                    :items="marketplaceOptions"
                    item-text="text"
                    item-value="value"
                    label="Pazaryeri"
                    prepend-icon="store"
                    dense
                    clearable
                  />
                </v-flex>
                <v-flex xs12 sm3 class="px-2 pt-3">
                  <v-btn small color="amber darken-3" class="white--text" @click="loadHistory">
                    <v-icon left small>refresh</v-icon>
                    Yenile
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-layout row wrap class="mb-3">
                <v-flex xs12 sm4 class="px-2">
                  <v-card color="amber lighten-4" flat class="pa-3">
                    <div class="caption grey--text text--darken-1">Toplam Alınan Has Gram (filtreye göre)</div>
                    <div class="title amber--text text--darken-4 font-weight-bold">{{ formatGram(historyTotalAcquiredGram) }} gr</div>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm4 class="px-2">
                  <v-card color="blue-grey lighten-5" flat class="pa-3">
                    <div class="caption grey--text text--darken-1">Anlaşma Sayısı</div>
                    <div class="title blue-grey--text text--darken-2 font-weight-bold">{{ filteredHistoryAgreements.length }}</div>
                  </v-card>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="historyHeaders"
                :items="filteredHistoryAgreements"
                :loading="loading"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-no-wrap">{{ formatDate(props.item.agreementDate) }}</td>
                    <td>
                      <v-chip small :color="agreementChipColor(props.item.status)" text-color="white">
                        {{ props.item.statusName }}
                      </v-chip>
                      <v-chip v-if="props.item.isPartial" x-small color="deep-purple lighten-1" text-color="white" class="ml-1"
                        :title="props.item.status === 1 ? 'Parçalanmış anlaşma olarak ödendi' : 'Parçalı anlaşma'">
                        Parçalı
                      </v-chip>
                    </td>
                    <td>
                      {{ props.item.companyName }}
                      <div v-if="props.item.ownerCompanyName" class="inv-meta">
                        🏢 {{ props.item.ownerCompanyName }}
                      </div>
                      <div v-if="agreementMarketplaces(props.item).length > 0" class="inv-meta">
                        <span
                          v-for="mp in agreementMarketplaces(props.item)"
                          :key="mp.mpId"
                          class="mr-1"
                          :class="marketplaceChipClass(mp.mpId)"
                        >{{ marketplaceLabel(mp) }} ×{{ mp.orderCount }}</span>
                      </div>
                    </td>
                    <td>
                      {{ props.item.contactPerson }}
                      <div v-if="props.item.createdByName" class="inv-meta">{{ props.item.createdByName }}</div>
                    </td>
                    <td class="text-no-wrap">
                      {{ formatDate(props.item.paymentDate) }}
                      <div class="inv-meta">{{ props.item.paidAt ? formatDate(props.item.paidAt) : '—' }}</div>
                    </td>
                    <td class="text-xs-right">{{ props.item.orderCount }}</td>
                    <td class="text-xs-right">{{ formatCurrency(props.item.totalNetCash) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatCurrency(props.item.agreedAmount) }}
                      <span v-if="agreementExtra(props.item) > 0" class="caption green--text d-block">
                        +{{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                      <span v-else-if="agreementExtra(props.item) < 0" class="caption red--text d-block">
                        {{ formatCurrency(agreementExtra(props.item)) }}
                      </span>
                    </td>
                    <td class="text-xs-right">{{ formatGramPrice(props.item.goldGramPrice) }}</td>
                    <td class="text-xs-right blue--text font-weight-bold">
                      {{ formatGram(agreementAcquiredGram(props.item)) }}
                    </td>
                    <td class="agr-action-cell">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="primary" v-on="on" @click="openDetailDialog(props.item)">
                            <v-icon small>visibility</v-icon>
                          </v-btn>
                        </template>
                        <span>Detay</span>
                      </v-tooltip>
                      <v-tooltip bottom v-if="props.item.isPartial">
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="indigo" v-on="on" @click="openChainDialog(props.item)">
                            <v-icon small>account_tree</v-icon>
                          </v-btn>
                        </template>
                        <span>Parçalı Zinciri Gör</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info">
                    Geçmiş kayıt yok.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <v-dialog v-model="createDialog" persistent max-width="640">
      <v-card v-if="createTarget">
        <v-card-title class="amber darken-3 white--text">
          <v-icon left color="white">{{ editingId ? 'edit' : 'handshake' }}</v-icon>
          <span v-if="editingId">
            Anlaşmayı Düzenle — {{ formatDateWithDay(createTarget.paymentDate) }}
          </span>
          <span v-else>
            {{ formatDateWithDay(createTarget.paymentDate) }}
            <span class="ml-2">— {{ createTarget.unallocatedOrderCount }} sipariş için Darphane Anlaşması</span>
          </span>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-card outlined class="pa-3 mb-3 grey lighten-4">
            <div class="body-2">
              <strong>Sipariş sayısı:</strong> {{ createTarget.unallocatedOrderCount }}
              <span v-if="createTarget.carriedOverOrderCount > 0" class="orange--text text--darken-2">
                ({{ createTarget.unallocatedOrderCount - createTarget.carriedOverOrderCount }} bugünün +
                <strong>{{ createTarget.carriedOverOrderCount }} devreden</strong>)
              </span>
            </div>
            <div
              v-if="createTarget.carriedOverOrderCount > 0"
              class="caption orange--text text--darken-2 mt-1"
            >
              ⓘ {{ createTarget.carriedOverOrderCount }} sipariş geçmişten devretti
              ({{ formatCurrency(createTarget.carriedOverNetCash) }} net cash) — bu anlaşmaya dahil edilecek.
            </div>
            <v-divider class="my-2" />
            <div class="body-2">
              <strong>Fatura toplamı:</strong> {{ formatCurrency(createTarget.unallocatedInvoiceAmount) }}
            </div>
            <div class="title blue--text font-weight-bold">
              Net hesaba yatacak: {{ formatCurrency(createTarget.unallocatedNetCash) }}
            </div>
            <div class="body-2 mt-1">
              <strong>Toplam has gram:</strong>
              {{ formatGram(unallocatedPureGram(createTarget)) }} gr
            </div>
          </v-card>

          <v-select
            v-model="createForm.refineryId"
            :items="refineries"
            item-text="name"
            item-value="id"
            label="Rafineri / Darphane"
            prepend-icon="factory"
            :rules="[v => !!v || 'Rafineri seçiniz']"
            no-data-text="Rafineri tanımlı değil (Admin > Rafineri Mutabakat)"
            required
            dense
          />
          <v-text-field
            v-model="createForm.contactPerson"
            label="Firmada Anlaşılan Yetkili"
            prepend-icon="person"
            :rules="[v => !!v && v.trim().length > 0 || 'Yetkili zorunludur']"
            maxlength="100"
            counter="100"
            required
            dense
          />
          <v-text-field
            v-model.number="createForm.goldGramPrice"
            label="Gram Has Altın Fiyatı (₺/gr)"
            prepend-icon="payments"
            type="number"
            min="0"
            step="0.01"
            :rules="[v => v > 0 || 'Gram fiyatı sıfırdan büyük olmalı']"
            required
            dense
          />
          <v-text-field
            v-model.number="createForm.agreedAmount"
            label="Anlaşılan Tutar (₺) — boş bırakırsanız sistem hesabı kullanılır"
            prepend-icon="account_balance_wallet"
            type="number"
            min="0"
            step="0.01"
            dense
          />
          <div class="caption grey--text text--darken-1 ml-4">
            Sistem hesabı: <strong>{{ formatCurrency(createTarget.unallocatedNetCash) }}</strong>
            <span v-if="agreedExtra !== null && agreedExtra !== 0" :class="agreedExtra > 0 ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
              · {{ agreedExtra > 0 ? 'Ekstra' : 'Eksik' }}:
              {{ agreedExtra > 0 ? '+' : '' }}{{ formatCurrency(agreedExtra) }}
            </span>
          </div>
          <div v-if="agreementGoldAcquired !== null" class="caption grey--text text--darken-1 ml-4 mt-1">
            Bu fiyatla anlaşırsanız: {{ formatCurrency(effectiveAgreedAmount) }} ÷ {{ formatGramPrice(createForm.goldGramPrice) }} ₺/gr =
            <strong>{{ formatGram(agreementGoldAcquired) }} gr</strong> has altın alırsınız.
            <span v-if="agreementGoldDelta !== null" :class="agreementGoldDelta >= 0 ? 'green--text' : 'red--text'">
              · Siparişlerin has gramı {{ formatGram(unallocatedPureGram(createTarget)) }} gr →
              {{ agreementGoldDelta >= 0 ? 'Kar' : 'Zarar' }}:
              <strong>{{ agreementGoldDelta >= 0 ? '+' : '' }}{{ formatGram(agreementGoldDelta) }} gr</strong>
            </span>
          </div>
          <v-textarea
            v-model="createForm.notes"
            label="Notlar (opsiyonel)"
            prepend-icon="notes"
            rows="2"
            auto-grow
            dense
          />
          <div class="caption grey--text text--darken-1 mt-2">
            Anlaşmayı kaydeden: <strong>{{ currentUserName }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="createDialog = false" :disabled="creating">İptal</v-btn>
          <v-btn
            color="success"
            :loading="creating"
            :disabled="!createFormValid"
            @click="submitCreate"
          >
            {{ editingId ? 'Güncelle' : 'Anlaşmayı Kaydet' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="markPaidDialog" persistent max-width="420">
      <v-card v-if="markPaidTarget">
        <v-card-title class="success white--text">
          <v-icon left color="white">check_circle</v-icon>
          Ödeme Onayı
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ markPaidTarget.companyName }}</strong> firmasına
            <strong>{{ formatCurrency(markPaidTarget.totalNetCash) }}</strong> ödemenizi
            onaylıyor musunuz?
          </div>
          <v-menu
            v-model="paidAtMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
            class="mt-3"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="markPaidForm.paidAt"
                label="Ödeme Tarihi (opsiyonel — boş bırakırsanız bugün)"
                prepend-icon="event"
                readonly
                v-on="on"
                dense
                clearable
                @click:clear="markPaidForm.paidAt = null"
              />
            </template>
            <v-date-picker
              v-model="markPaidForm.paidAt"
              locale="tr-TR"
              @input="paidAtMenu = false"
            />
          </v-menu>
          <v-select
            v-model="markPaidForm.cashAccountId"
            :items="cashAccountOptions"
            item-text="name"
            item-value="id"
            label="Ödeme Hesabı *"
            prepend-icon="account_balance"
            :rules="[v => !!v || 'Hesap seçiniz']"
            no-data-text="Aktif hesap bulunamadı"
            required
            dense
            class="mt-2"
          />
          <div class="caption grey--text text--darken-1 ml-4">
            Ödeme TL olarak bu hesaptan düşülecektir.
            <span v-if="markPaidAccountTlBalance !== null">
              · Mevcut bakiye: <strong>{{ formatCurrency(markPaidAccountTlBalance) }}</strong>
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="markPaidDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="success" :loading="processing" @click="submitMarkPaid">
            Ödendi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="partialPayDialog" persistent max-width="500">
      <v-card v-if="partialTarget">
        <v-card-title class="deep-purple white--text">
          <v-icon left color="white">call_split</v-icon>Parçalı Ödeme
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2 mb-2">
            <strong>{{ partialTarget.companyName }}</strong> — Anlaşma:
            <strong>{{ formatCurrency(partialTarget.agreedAmount) }}</strong> /
            <strong>{{ formatGram(partialTarget.totalPureGram) }}</strong>
          </div>
          <v-text-field v-model.number="partialForm.paidAmount" label="Ödenen Tutar" type="number" min="0" step="0.01" prefix="₺" dense></v-text-field>
          <v-menu v-model="deferMenu" :close-on-content-click="false" lazy transition="scale-transition" offset-y full-width min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field :value="partialForm.deferDate" label="Kalan İçin Yeni (Erteleme) Tarihi *" prepend-icon="event" readonly v-on="on" dense></v-text-field>
            </template>
            <v-date-picker v-model="partialForm.deferDate" locale="tr-TR" @input="deferMenu = false"></v-date-picker>
          </v-menu>
          <v-select
            v-model="partialForm.cashAccountId"
            :items="cashAccountOptions"
            item-text="name"
            item-value="id"
            label="Hangi Hesaptan Ödensin *"
            prepend-icon="account_balance"
            :rules="[v => !!v || 'Hesap seçiniz']"
            no-data-text="Aktif hesap bulunamadı"
            required
            dense
          ></v-select>
          <div class="caption grey--text text--darken-1 ml-4">
            Ödeme TL olarak bu hesaptan düşülecektir.
            <span v-if="partialPayAccountTlBalance !== null">
              · Mevcut bakiye: <strong>{{ formatCurrency(partialPayAccountTlBalance) }}</strong>
            </span>
          </div>
          <v-divider class="my-2"></v-divider>
          <div class="body-2">Karşılığı alınan altın: <strong class="green--text">{{ formatGram(partialPaidGram) }}</strong></div>
          <div class="body-2">Kalan (yeni anlaşma): <strong class="red--text">{{ formatCurrency(partialRemainingAmount) }}</strong> / <strong class="red--text">{{ formatGram(partialRemainingGram) }}</strong></div>
          <div v-if="partialError" class="red--text caption mt-2">{{ partialError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="partialPayDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="deep-purple" dark :loading="processing" :disabled="!partialValid" @click="submitPartialPay">Parçalı Öde</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="chainDialog" max-width="920">
      <v-card>
        <v-card-title class="indigo white--text">
          <v-icon left color="white">account_tree</v-icon>Parçalı Anlaşma Zinciri
          <v-spacer></v-spacer>
          <v-btn icon dark @click="chainDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-3">
          <div v-if="chainLoading" class="text-xs-center pa-3 grey--text">Yükleniyor...</div>
          <v-data-table v-else :headers="chainHeaders" :items="chainItems" hide-actions class="elevation-1">
            <template v-slot:items="p">
              <td>#{{ p.item.id }}</td>
              <td><v-chip small :color="agreementChipColor(p.item.status)" text-color="white">{{ p.item.statusName }}</v-chip></td>
              <td class="text-no-wrap">{{ formatDate(p.item.paymentDate) }}</td>
              <td class="text-no-wrap">{{ p.item.paidAt ? formatDate(p.item.paidAt) : '—' }}</td>
              <td class="text-xs-right">{{ formatCurrency(p.item.agreedAmount) }}</td>
              <td class="text-xs-right">{{ formatGram(p.item.totalPureGram) }}</td>
              <td>{{ p.item.parentAgreementId ? ('#' + p.item.parentAgreementId + '’den türedi') : 'Kök anlaşma' }}</td>
            </template>
          </v-data-table>
          <div class="mt-2 text-xs-right caption">
            Toplam: <strong>{{ formatCurrency(chainTotalAmount) }}</strong> / <strong>{{ formatGram(chainTotalGram) }}</strong>
            &nbsp;·&nbsp; Ödenen altın: <strong class="green--text">{{ formatGram(chainPaidGram) }}</strong>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" persistent max-width="420">
      <v-card v-if="cancelTarget">
        <v-card-title class="error white--text">
          <v-icon left color="white">cancel</v-icon>
          Anlaşmayı İptal Et
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ cancelTarget.companyName }}</strong> ile yapılan
            <strong>{{ formatCurrency(cancelTarget.totalNetCash) }}</strong> tutarındaki anlaşmayı iptal etmek üzeresiniz.
            <br />
            Anlaşmaya bağlı <strong>{{ cancelTarget.orderCount }}</strong> sipariş tekrar anlaşılmamış havuzuna dönecek.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="cancelDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="error" :loading="processing" @click="submitCancel">
            İptal Et
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <mint-agreement-detail-dialog
      v-model="detailDialog"
      :agreement-id="detailAgreementId"
      @error="showError"
    />

    <v-dialog v-model="dayOrdersDialog" persistent max-width="1100">
      <v-card v-if="dayOrdersData">
        <v-card-title class="primary white--text">
          <v-icon left color="white">visibility</v-icon>
          {{ formatDateWithDay(dayOrdersData.paymentDate) }} — Siparişler
          <v-spacer />
          <v-btn icon dark @click="dayOrdersDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-layout row wrap class="mb-2">
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Toplam Sipariş</div>
              <div class="subtitle-1 font-weight-bold">{{ dayOrdersData.orderCount }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Anlaşılmış / Bekleyen</div>
              <div class="subtitle-1">
                <span class="green--text font-weight-bold">{{ dayOrdersData.allocatedOrderCount }}</span>
                /
                <span class="orange--text font-weight-bold">{{ dayOrdersData.unallocatedOrderCount }}</span>
              </div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Fatura Toplamı</div>
              <div class="subtitle-1">{{ formatCurrency(dayOrdersData.totalInvoiceAmount) }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2">
              <div class="caption grey--text text--darken-1">Net Hesaba Yatacak</div>
              <div class="subtitle-1 blue--text font-weight-bold">
                {{ formatCurrency(dayOrdersData.totalNetCash) }}
              </div>
            </v-flex>
          </v-layout>

          <v-data-table
            :headers="dayOrderHeaders"
            :items="dayOrdersData.orders || []"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.orderNumber }}</td>
                <td>
                  <span v-if="props.item.mpName" class="inv-chip" :class="marketplaceChipClass(props.item.mpId)">{{ props.item.mpName }}</span>
                  <div v-if="props.item.ownerCompanyName" class="inv-meta">{{ props.item.ownerCompanyName }}</div>
                </td>
                <td class="text-no-wrap">{{ formatDate(props.item.completedDate) }}</td>
                <td>{{ props.item.customerName }}</td>
                <td>{{ props.item.shippingCity }}</td>
                <td class="text-xs-right">{{ formatCurrency(props.item.invoiceAmount) }}</td>
                <td class="text-xs-right">{{ formatCurrency(props.item.netCash) }}</td>
                <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }} gr</td>
                <td>
                  <v-chip
                    v-if="props.item.mintAgreementId"
                    small
                    color="green"
                    text-color="white"
                    style="cursor: pointer"
                    @click="openDetailDialog({ id: props.item.mintAgreementId })"
                  >
                    Anlaşılı · Anlaşma #{{ props.item.mintAgreementId }}
                  </v-chip>
                  <v-chip
                    v-else-if="props.item.isCarriedOver"
                    small
                    color="orange"
                    text-color="white"
                  >
                    🔄 Devreden
                  </v-chip>
                  <v-chip v-else small color="grey lighten-1" text-color="white">
                    Bekleyen
                  </v-chip>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <v-alert :value="true" color="info" icon="info">
                Bu güne ait sipariş yok.
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="dayOrdersDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" top>
      {{ snackbarMessage }}
      <v-btn flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import { authenticationService } from '@/_services';
import MintAgreementDetailDialog from '@/components/MintAgreementDetailDialog.vue';

export default {
  name: 'AdminMintAgreements',
  components: { MintAgreementDetailDialog },
  data() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const plus14 = new Date(today);
    plus14.setDate(plus14.getDate() + 14);
    return {
      activeTab: 0,
      loading: false,

      planFrom: this.toIsoDate(tomorrow),
      planTo: this.toIsoDate(plus14),
      planFromMenu: false,
      planToMenu: false,

      historyFrom: null,
      historyTo: null,
      historyFromMenu: false,
      historyToMenu: false,
      historyStatus: 1,
      historyRefineryId: null,
      historyStatusOptions: [
        { text: 'Ödendi', value: 1 },
        { text: 'İptal Edildi', value: 2 },
      ],

      marketplaceFilter: null,
      marketplaceOptions: [
        { text: 'Tümü', value: null },
        { text: 'Hepsiburada', value: 1 },
        { text: 'Alışgidiş', value: 2 },
        { text: 'Trendyol', value: 3 },
        { text: 'Idefix', value: 4 },
        { text: 'Idia', value: 5 },
      ],

      createDialog: false,
      createTarget: null,
      creating: false,
      editingId: null,
      createForm: {
        refineryId: null,
        companyName: '',
        contactPerson: '',
        goldGramPrice: 0,
        agreedAmount: null,
        notes: '',
      },

      cashAccounts: [],

      markPaidDialog: false,
      markPaidTarget: null,
      markPaidForm: { paidAt: null, cashAccountId: null },
      paidAtMenu: false,

      partialPayDialog: false,
      partialTarget: null,
      partialForm: { paidAmount: null, deferDate: null, cashAccountId: null },
      deferMenu: false,
      partialError: '',

      chainDialog: false,
      chainLoading: false,
      chainItems: [],
      chainHeaders: [
        { text: '#', value: 'id', sortable: false },
        { text: 'Durum', value: 'status', sortable: false },
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Ödendi', value: 'paidAt', sortable: false },
        { text: 'Tutar', value: 'agreedAmount', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Bağlantı', value: 'parentAgreementId', sortable: false },
      ],

      cancelDialog: false,
      cancelTarget: null,
      processing: false,

      detailDialog: false,
      detailAgreementId: null,

      dayOrdersDialog: false,
      dayOrdersData: null,
      dayOrderHeaders: [
        { text: 'Sipariş No', value: 'orderNumber', sortable: false },
        { text: 'Pazaryeri', value: 'mpName', sortable: false },
        { text: 'Tamamlanma', value: 'completedDate', sortable: false },
        { text: 'Müşteri', value: 'customerName', sortable: false },
        { text: 'Şehir', value: 'shippingCity', sortable: false },
        { text: 'Fatura', value: 'invoiceAmount', sortable: false, align: 'right' },
        { text: 'Net Cash', value: 'netCash', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Durum', value: 'status', sortable: false },
      ],

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      snackbarTimeout: 4000,

      planHeaders: [
        { text: 'Ödeme Günü', value: 'paymentDate', sortable: false },
        { text: 'Pazaryeri', value: 'marketplaces', sortable: false },
        { text: 'Sipariş #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Fatura', value: 'totalInvoiceAmount', sortable: false, align: 'right' },
        { text: 'Net Cash (₺)', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
        { text: 'Anlaşma Durumu', value: 'agreementStatus', sortable: false },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
      agreementHeaders: [
        { text: 'Tarih', value: 'agreementDate', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili / Kaydeden', value: 'contactPerson', sortable: false },
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Sip. #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Fatura / Net Cash', value: 'totalInvoiceAmount', sortable: false, align: 'right' },
        { text: 'Anlaşılan', value: 'agreedAmount', sortable: false, align: 'right' },
        { text: '₺/gr', value: 'goldGramPrice', sortable: false, align: 'right' },
        { text: 'Sip. / Alınacak gr', value: 'acquiredGram', sortable: false, align: 'right' },
        { text: 'İşlem', value: 'action', sortable: false, align: 'center' },
      ],
      historyHeaders: [
        { text: 'Tarih', value: 'agreementDate', sortable: false },
        { text: 'Durum', value: 'statusName', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili / Kaydeden', value: 'contactPerson', sortable: false },
        { text: 'Vade / Ödendi', value: 'paymentDate', sortable: false },
        { text: 'Sip. #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Net Cash', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Anlaşılan', value: 'agreedAmount', sortable: false, align: 'right' },
        { text: '₺/gr', value: 'goldGramPrice', sortable: false, align: 'right' },
        { text: 'Alınacak', value: 'acquiredGram', sortable: false, align: 'right' },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
      overdueHeaders: [
        { text: 'Vade', value: 'paymentDate', sortable: false },
        { text: 'Geciken', value: 'daysOverdue', sortable: false },
        { text: 'Firma', value: 'companyName', sortable: false },
        { text: 'Yetkili', value: 'contactPerson', sortable: false },
        { text: 'Net Cash', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Sipariş #', value: 'orderCount', sortable: false, align: 'right' },
        { text: '', value: 'action', sortable: false },
      ],
    };
  },
  computed: {
    cashAccountOptions() {
      return (this.cashAccounts || []).map((a) => ({
        id: a.id,
        name: `${a.name} (${a.accountType === 0 ? 'Banka' : 'Kasa'})`,
      }));
    },
    markPaidAccountTlBalance() {
      return this.accountTlBalance(this.markPaidForm.cashAccountId);
    },
    partialPayAccountTlBalance() {
      return this.accountTlBalance(this.partialForm.cashAccountId);
    },
    partialPaidGram() {
      const t = this.partialTarget;
      const amt = Number(this.partialForm.paidAmount) || 0;
      if (!t || !(Number(t.agreedAmount) > 0) || amt <= 0) return 0;
      return (Number(t.totalPureGram) || 0) * (amt / Number(t.agreedAmount));
    },
    partialRemainingAmount() {
      const t = this.partialTarget;
      if (!t) return 0;
      return (Number(t.agreedAmount) || 0) - (Number(this.partialForm.paidAmount) || 0);
    },
    partialRemainingGram() {
      const t = this.partialTarget;
      if (!t) return 0;
      return (Number(t.totalPureGram) || 0) - this.partialPaidGram;
    },
    partialValid() {
      const t = this.partialTarget;
      if (!t) return false;
      const amt = Number(this.partialForm.paidAmount) || 0;
      return amt > 0 && amt < Number(t.agreedAmount) && !!this.partialForm.deferDate && !!this.partialForm.cashAccountId;
    },
    chainTotalAmount() {
      return (this.chainItems || []).reduce((s, x) => s + (Number(x.agreedAmount) || 0), 0);
    },
    chainTotalGram() {
      return (this.chainItems || []).reduce((s, x) => s + (Number(x.totalPureGram) || 0), 0);
    },
    chainPaidGram() {
      return (this.chainItems || []).filter((x) => x.status === 1).reduce((s, x) => s + (Number(x.totalPureGram) || 0), 0);
    },
    plan() {
      return this.$store.getters['mintAgreements/plan'];
    },
    pendingAgreements() {
      return this.$store.getters['mintAgreements/pendingAgreements'];
    },
    historyAgreements() {
      return this.$store.getters['mintAgreements/paidOrCancelledAgreements'];
    },
    historyRefineryOptions() {
      const opts = (this.refineries || []).map((r) => ({ text: r.name, value: r.id }));
      return [{ text: 'Tümü', value: null }, ...opts];
    },
    filteredPendingAgreements() {
      return (this.pendingAgreements || []).filter((a) => this.matchesMarketplaceFilter(a));
    },
    filteredHistoryAgreements() {
      const list = (this.historyAgreements || []).filter((a) => this.matchesMarketplaceFilter(a));
      if (this.historyRefineryId == null) return list;
      return list.filter((a) => a.refineryId === this.historyRefineryId);
    },
    historyTotalAcquiredGram() {
      return (this.filteredHistoryAgreements || [])
        .reduce((s, a) => s + this.agreementAcquiredGram(a), 0);
    },
    overdueTotal() {
      return (this.overdueAgreements || []).reduce((t, a) => t + (Number(a.totalNetCash) || 0), 0);
    },
    overdueAgreements() {
      return (this.plan && this.plan.overdueAgreements) || [];
    },
    computeByCompany() {
      return !!(this.plan && this.plan.computeByCompany);
    },
    currentUserName() {
      const u = authenticationService.currentUserValue;
      if (!u) return '—';
      return u.firstName || u.username || u.userName || '—';
    },
    createFormValid() {
      return (
        !!this.createForm.refineryId &&
        !!this.createForm.contactPerson && this.createForm.contactPerson.trim().length > 0 &&
        Number(this.createForm.goldGramPrice) > 0
      );
    },
    refineries() {
      return this.$store.getters['refineries/refineries'];
    },
    effectiveAgreedAmount() {
      if (!this.createTarget) return 0;
      const manual = Number(this.createForm.agreedAmount);
      if (this.createForm.agreedAmount !== null && this.createForm.agreedAmount !== '' && manual > 0) {
        return manual;
      }
      return Number(this.createTarget.unallocatedNetCash) || 0;
    },
    agreedExtra() {
      if (!this.createTarget) return null;
      const manual = Number(this.createForm.agreedAmount);
      if (this.createForm.agreedAmount === null || this.createForm.agreedAmount === '' || manual <= 0) {
        return null;
      }
      const systemCash = Number(this.createTarget.unallocatedNetCash) || 0;
      return manual - systemCash;
    },
    agreementGoldAcquired() {
      const cash = this.effectiveAgreedAmount;
      const price = Number(this.createForm.goldGramPrice);
      if (!cash || !price || price <= 0) return null;
      return cash / price;
    },
    agreementGoldDelta() {
      if (this.agreementGoldAcquired === null) return null;
      const orderGram = this.unallocatedPureGram(this.createTarget);
      if (!orderGram) return null;
      return this.agreementGoldAcquired - orderGram;
    },
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 0) this.loadPlan();
      else if (newVal === 1) this.loadPending();
      else if (newVal === 2) this.loadHistory();
    },
    '$route.query.tab'() {
      this.applyTabFromRoute();
    },
  },
  mounted() {
    this.applyTabFromRoute();
    if (this.activeTab === 0) this.loadPlan();
    this.$store.dispatch('refineries/getRefineries');
    this.loadCashAccounts();
  },
  methods: {
    applyTabFromRoute() {
      const tab = this.$route.query.tab;
      if (tab === 'pending') this.activeTab = 1;
      else if (tab === 'history') this.activeTab = 2;
      else if (tab === 'plan') this.activeTab = 0;
    },
    async loadPlan() {
      try {
        await this.$store.dispatch('mintAgreements/getUpcomingPlan', {
          from: this.planFrom,
          to: this.planTo,
        });
      } catch (e) {
        this.showError(e);
      }
    },
    async loadPending() {
      try {
        await this.$store.dispatch('mintAgreements/getPending');
      } catch (e) {
        this.showError(e);
      }
    },
    async loadHistory() {
      try {
        const filters = {};
        if (this.historyFrom) filters.from = this.historyFrom;
        if (this.historyTo) filters.to = this.historyTo;
        if (this.historyStatus !== null && this.historyStatus !== undefined) {
          filters.status = this.historyStatus;
        }
        await this.$store.dispatch('mintAgreements/getHistory', filters);
      } catch (e) {
        this.showError(e);
      }
    },
    async loadCashAccounts(companyId) {
      try {
        this.cashAccounts = (await this.$store.dispatch('cashAccounts/listCashAccounts', {
          companyId: companyId || null,
          status: 'ACTIVE',
        })) || [];
      } catch (e) {
        this.cashAccounts = [];
      }
    },
    accountTlBalance(accountId) {
      if (!accountId) return null;
      const acc = (this.cashAccounts || []).find((a) => a.id === accountId);
      if (!acc || !acc.balances) return null;
      const tl = acc.balances.find((b) => b.code === 'TL');
      return tl ? Number(tl.balance) || 0 : null;
    },

    openCreateDialog(day) {
      this.editingId = null;
      this.createTarget = day;
      this.createForm = {
        refineryId: null,
        companyName: '',
        contactPerson: '',
        goldGramPrice: 0,
        agreedAmount: null,
        notes: '',
      };
      this.createDialog = true;
    },
    openEditDialog(agreement) {
      this.editingId = agreement.id;
      this.createTarget = {
        paymentDate: agreement.paymentDate,
        unallocatedOrderCount: agreement.orderCount,
        unallocatedNetCash: agreement.totalNetCash,
        unallocatedInvoiceAmount: agreement.totalInvoiceAmount,
        totalPureGram: agreement.totalPureGram,
        carriedOverOrderCount: 0,
        carriedOverNetCash: 0,
        existingAgreements: [],
      };
      this.createForm = {
        refineryId: agreement.refineryId || null,
        companyName: agreement.companyName,
        contactPerson: agreement.contactPerson,
        goldGramPrice: agreement.goldGramPrice,
        agreedAmount: Number(agreement.agreedAmount) === Number(agreement.totalNetCash) ? null : agreement.agreedAmount,
        notes: agreement.notes || '',
      };
      this.createDialog = true;
    },
    async submitCreate() {
      if (!this.createFormValid || !this.createTarget) return;
      this.creating = true;
      try {
        const payload = {
          goldGramPrice: Number(this.createForm.goldGramPrice),
          refineryId: this.createForm.refineryId,
          contactPerson: this.createForm.contactPerson.trim(),
          notes: this.createForm.notes ? this.createForm.notes.trim() : null,
        };
        if (this.createForm.agreedAmount !== null && this.createForm.agreedAmount !== '' && Number(this.createForm.agreedAmount) > 0) {
          payload.agreedAmount = Number(this.createForm.agreedAmount);
        }
        if (this.editingId) {
          await this.$store.dispatch('mintAgreements/updateAgreement', { id: this.editingId, payload });
          this.showSuccess('Anlaşma güncellendi');
        } else {
          payload.paymentDate = this.createTarget.paymentDate;
          await this.$store.dispatch('mintAgreements/createAgreement', payload);
          this.showSuccess(`Anlaşma kaydedildi (${this.createTarget.unallocatedOrderCount} sipariş bağlandı)`);
        }
        this.createDialog = false;
        await this.loadPlan();
        await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.creating = false;
      }
    },

    openMarkPaidDialog(agreement) {
      this.markPaidTarget = agreement;
      this.markPaidForm = { paidAt: null, cashAccountId: null };
      this.markPaidDialog = true;
      this.loadCashAccounts(agreement.companyId);
    },
    async submitMarkPaid() {
      if (!this.markPaidTarget) return;
      if (!this.markPaidForm.cashAccountId) {
        this.$store.state.message = 'Lütfen ödemenin yapılacağı hesabı seçin.';
        this.$store.state.dialog = true;
        return;
      }
      this.processing = true;
      try {
        await this.$store.dispatch('mintAgreements/markPaid', {
          id: this.markPaidTarget.id,
          paidAt: this.markPaidForm.paidAt,
          cashAccountId: this.markPaidForm.cashAccountId,
        });
        this.markPaidDialog = false;
        this.showSuccess('Anlaşma ödendi olarak işaretlendi');
        await this.loadPlan();
        await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },

    openPartialPayDialog(agreement) {
      this.partialTarget = agreement;
      this.partialForm = { paidAmount: null, deferDate: null, cashAccountId: null };
      this.partialError = '';
      this.partialPayDialog = true;
      this.loadCashAccounts(agreement.companyId);
    },
    async submitPartialPay() {
      if (!this.partialValid || !this.partialTarget) return;
      if (!this.partialForm.cashAccountId) {
        this.$store.state.message = 'Lütfen ödemenin yapılacağı hesabı seçin.';
        this.$store.state.dialog = true;
        return;
      }
      this.processing = true;
      this.partialError = '';
      try {
        await this.$store.dispatch('mintAgreements/partialPay', {
          id: this.partialTarget.id,
          payload: {
            paidAmount: Number(this.partialForm.paidAmount),
            deferDate: this.partialForm.deferDate,
            cashAccountId: this.partialForm.cashAccountId,
          },
        });
        this.partialPayDialog = false;
        this.showSuccess('Parçalı ödeme kaydedildi, kalan için yeni anlaşma oluşturuldu');
        await this.loadPlan();
        await this.loadPending();
        await this.loadHistory();
      } catch (e) {
        this.partialError = (e && e.response && e.response.data && e.response.data.message) || 'Parçalı ödeme başarısız.';
      } finally {
        this.processing = false;
      }
    },
    async openChainDialog(agreement) {
      this.chainDialog = true;
      this.chainLoading = true;
      this.chainItems = [];
      try {
        this.chainItems = (await this.$store.dispatch('mintAgreements/getChain', agreement.id)) || [];
      } catch (e) {
        this.chainItems = [];
      } finally {
        this.chainLoading = false;
      }
    },
    openCancelDialog(agreement) {
      this.cancelTarget = agreement;
      this.cancelDialog = true;
    },
    async submitCancel() {
      if (!this.cancelTarget) return;
      this.processing = true;
      try {
        await this.$store.dispatch('mintAgreements/cancelAgreement', this.cancelTarget.id);
        this.cancelDialog = false;
        this.showSuccess('Anlaşma iptal edildi, siparişler havuza döndü');
        await this.loadPlan();
        await this.loadPending();
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },

    openDetailDialog(agreement) {
      this.detailAgreementId = agreement.id;
      this.detailDialog = true;
    },

    openDayOrdersDialog(day) {
      this.dayOrdersData = day;
      this.dayOrdersDialog = true;
    },

    toIsoDate(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString('tr-TR');
    },
    formatDateWithDay(v) {
      if (!v) return '—';
      const d = new Date(v);
      const dayName = d.toLocaleDateString('tr-TR', { weekday: 'short' });
      return `${d.toLocaleDateString('tr-TR')} ${dayName}`;
    },
    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);
    },
    formatGram(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
    },
    formatGramPrice(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    isToday(v) {
      if (!v) return false;
      const d = new Date(v);
      const t = new Date();
      return d.toDateString() === t.toDateString();
    },
    planRowClass(day) {
      if (!day) return '';
      const today = this.isToday(day.paymentDate);
      const hasPending = (day.unallocatedOrderCount || 0) > 0;
      if (hasPending) return today ? 'orange lighten-4' : 'orange lighten-5';
      if (today) return 'amber lighten-5';
      return '';
    },
    unallocatedPureGram(day) {
      const allocPure = (day.existingAgreements || []).reduce((s, a) => s + (a.totalPureGram || 0), 0);
      return Math.max(0, (day.totalPureGram || 0) - allocPure);
    },
    agreementMarketplaces(agreement) {
      return (agreement && agreement.marketplaces) || [];
    },
    matchesMarketplaceFilter(agreement) {
      if (this.marketplaceFilter === null || this.marketplaceFilter === undefined) return true;
      return this.agreementMarketplaces(agreement)
        .some((m) => m.mpId === this.marketplaceFilter);
    },
    marketplaceLabel(mp) {
      if (!mp) return '';
      if (mp.mpName) return mp.mpName;
      const found = this.marketplaceOptions.find((o) => o.value === mp.mpId);
      return found ? found.text : `Pazaryeri ${mp.mpId}`;
    },
    planCompanyLabel(day) {
      const names = (day.marketplaces || [])
        .map((m) => m.ownerCompanyName)
        .filter((n) => !!n);
      const uniq = [...new Set(names)];
      return uniq.length ? uniq.join(' · ') : '';
    },
    marketplaceChipClass(mpId) {
      if (mpId === 1) return 'inv-chip inv-chip--brown';
      if (mpId === 3) return 'inv-chip inv-chip--purple';
      if (mpId === 4) return 'inv-chip inv-chip--blue';
      if (mpId === 5) return 'inv-chip inv-chip--teal';
      return 'inv-chip inv-chip--grey';
    },
    agreementChipColor(status) {
      if (status === 0) return 'orange';
      if (status === 1) return 'green';
      return 'grey';
    },
    agreementExtra(agreement) {
      if (!agreement) return 0;
      const agreed = Number(agreement.agreedAmount) || 0;
      const net = Number(agreement.totalNetCash) || 0;
      return agreed - net;
    },
    agreementAcquiredGram(agreement) {
      if (!agreement) return 0;
      const agreed = Number(agreement.agreedAmount) || 0;
      const price = Number(agreement.goldGramPrice) || 0;
      if (!agreed || !price) return 0;
      return agreed / price;
    },
    agreementAcquiredDelta(agreement) {
      const acquired = this.agreementAcquiredGram(agreement);
      const sipGram = Number(agreement && agreement.totalPureGram) || 0;
      if (!acquired || !sipGram) return 0;
      return acquired - sipGram;
    },
    showSuccess(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = 'success';
      this.snackbarTimeout = 4000;
      this.snackbar = true;
    },
    showError(e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Bir hata oluştu';
      this.snackbarMessage = msg;
      this.snackbarColor = 'error';
      this.snackbarTimeout = 8000;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.transparent {
  background: transparent !important;
}

.v-datatable >>> td,
.v-datatable >>> th {
  padding: 0 10px !important;
}
.v-datatable >>> td {
  font-size: 13px;
  height: auto !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.v-datatable >>> th {
  font-size: 12px !important;
  white-space: nowrap;
}
.v-datatable >>> td .v-chip {
  margin: 2px 3px 2px 0;
}
.v-datatable >>> td .v-chip .v-chip__content {
  padding: 0 9px;
  font-size: 11.5px;
}
.v-datatable >>> td .v-btn--icon {
  width: 30px;
  height: 30px;
  margin: 0 2px;
}
.v-datatable >>> .inv-meta {
  line-height: 1.35;
}
.v-datatable >>> .inv-chip {
  padding: 1px 6px;
  font-size: 10.5px;
}
.od-card { border-top: 3px solid #c62828; }
.od-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 8px;
  margin-left: 10px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.25);
  font-size: 12.5px;
  font-weight: 700;
}
.od-sum { display: inline-flex; align-items: baseline; gap: 6px; }
.od-sum-k { font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; opacity: 0.85; }
.od-sum-v { font-family: Menlo, Consolas, monospace; font-size: 15px; font-weight: 700; }
.od-list { padding: 4px 0; }
.od-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #f2f4f5;
  flex-wrap: wrap;
}
.od-row:last-child { border-bottom: 0; }
.od-days {
  flex-shrink: 0;
  width: 54px;
  text-align: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 4px 0;
}
.od-days-n { font-size: 19px; font-weight: 700; color: #c62828; line-height: 1.1; font-family: Menlo, Consolas, monospace; }
.od-days-l { font-size: 10.5px; color: #c62828; text-transform: uppercase; letter-spacing: 0.5px; }
.od-main { flex: 1; min-width: 160px; }
.od-firm { font-size: 15px; font-weight: 700; color: #263238; }
.od-person { font-size: 12.5px; font-weight: 400; color: #78909c; margin-left: 8px; }
.od-meta { font-size: 12px; color: #90a4ae; margin-top: 2px; }
.od-cash { text-align: right; min-width: 120px; }
.od-cash-v { font-family: Menlo, Consolas, monospace; font-size: 15px; font-weight: 700; color: #37474f; }
.od-cash-l { font-size: 10.5px; color: #90a4ae; text-transform: uppercase; letter-spacing: 0.5px; }
.od-actions { display: flex; align-items: center; flex-shrink: 0; }
.od-btn { text-transform: none; font-weight: 600; border-radius: 6px; }
.plan-mp-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 1px 0;
}
.plan-mp-cash {
  font-family: Menlo, Consolas, monospace;
  font-size: 11.5px;
  font-weight: 600;
  color: #37474f;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.plan-mp-cell {
  min-width: 178px;
  max-width: 260px;
}
.plan-status-cell {
  min-width: 170px;
}
.plan-status-cell >>> .v-chip {
  margin: 2px 0 !important;
}
.plan-action-cell {
  width: 148px;
  white-space: nowrap;
}
.plan-action-cell >>> .plan-action-btn {
  display: flex;
  width: 132px;
  margin: 3px 0 !important;
  font-size: 11.5px;
}
.agr-action-cell {
  width: 96px;
  text-align: center;
}
.v-datatable >>> td.text-xs-right,
.v-datatable >>> td.text-no-wrap {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.v-datatable >>> td .v-btn--icon {
  width: 28px;
  height: 28px;
  margin: 0 1px;
}
</style>
